import { t as uiText } from '../../i18n';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { storage, db } from '../../lib/firebase';

export interface UpdateWorkspaceIconParams {
  workspaceId: string;
  imageFile: File | null;
  currentIconPath?: string | null;
  onProgress?: (progress: number) => void;
}

export const updateWorkspaceIcon = async ({
  workspaceId,
  imageFile,
  currentIconPath,
  onProgress
}: UpdateWorkspaceIconParams): Promise<string | null> => {
  try {
    // 進捗を報告
    onProgress?.(10);

    // 画像ファイルがない場合は、既存のアイコンを削除
    if (!imageFile) {
      // 既存のアイコンがある場合は削除
      if (currentIconPath) {
        try {
          const oldIconRef = ref(storage, currentIconPath);
          await deleteObject(oldIconRef);
        } catch (error) {
          console.warn('🔄 updateWorkspaceIcon: 既存アイコンの削除に失敗（既に存在しない可能性）:', error);
        }
      }

      // Firestoreからアイコンパスを削除
      const workspaceRef = doc(db, 'workspaces', workspaceId);
      await updateDoc(workspaceRef, {
        iconPath: null,
        updatedAt: new Date()
      });

      return null;
    }

    // 新しい画像をアップロード
    const timestamp = Date.now();
    const fileName = `${workspaceId}/workspace-icons/${timestamp}_${imageFile.name}`;
    const storageRef = ref(storage, fileName);

    onProgress?.(30);

    // 画像をアップロード
    const snapshot = await uploadBytes(storageRef, imageFile);

    onProgress?.(70);

    // パスを保存（URLではなく）
    const iconPath = fileName;

    // 既存のアイコンがある場合は削除
    if (currentIconPath) {
      try {
        const oldIconRef = ref(storage, currentIconPath);
        await deleteObject(oldIconRef);
      } catch (error) {
        console.warn('🔄 updateWorkspaceIcon: 既存アイコンの削除に失敗（既に存在しない可能性）:', error);
      }
    }

    onProgress?.(90);

    // Firestoreにアイコンパスを保存
    const workspaceRef = doc(db, 'workspaces', workspaceId);
    await updateDoc(workspaceRef, {
      iconPath: iconPath,
      updatedAt: new Date()
    });

    onProgress?.(100);

    // 表示用のURLを生成して返す
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error('🔄 updateWorkspaceIcon: エラーが発生しました:', error);
    throw new Error(uiText("Failed to update the workspace icon"));
  }
}; 